import { collapsePath, sanitize } from "./params"

// Firebase Analytics (GA4) for the operator console, built the way corelite-dash's analyticsService is so
// the front ends report the same way (phase 1.17). Firebase only: corelite-dash's Clarity and warehouse
// sinks can be added later behind the same trackEvent.
//
// No config, no analytics: without the `analytics` shared prop (Admin::BaseController) every call here
// does nothing, and the Firebase SDK is never even downloaded - it is imported only once there is a
// config to start it with.

const MAX_QUEUED_EVENTS = 20

let firebase = null // firebase/analytics, once loaded
let analytics = null // its instance, once initialised
let initPromise = null
let userId = null
let userRole = null
// What the current page knows, stamped on every event: which organization and app, and where.
let context = {}
// The current page component, which is also what Firebase is told the page's title is.
let screen = null

// isSupported() is async, so events fired while it resolves - the first screen_view, a click on a
// slow connection - are held and replayed in order rather than lost.
let pending = []

const isBrowser = () => typeof window !== "undefined"

// The caller's params first and the context over them, so a call site cannot overwrite the page's
// own organization or app by passing a stale one. A context value the page does not have (null) leaves
// the caller's: after deleting an app, the page that comes back has no app, and the event still names
// the one deleted.
const enrich = (params) => {
  const known = Object.fromEntries(Object.entries(context).filter(([, value]) => value != null))
  return sanitize({ ...params, ...known })
}

// Firebase records the page's URL and title on every event. The URL can hold a token (an invitation
// link) and a query string, and a title can hold a name, so both are replaced with the route pattern.
const pageDefaults = () => ({
  page_location: `${window.location.origin}${context.page_path ?? collapsePath(window.location.pathname)}`,
  page_title: screen ?? "console",
})

const send = (name, params) => {
  if (!analytics) return false

  firebase.logEvent(analytics, name, params)
  return true
}

const flush = () => {
  const queued = pending
  pending = []
  queued.forEach(({ name, params }) => send(name, params))
}

const identify = () => {
  if (!analytics) return

  firebase.setUserId(analytics, userId)
  if (userId) firebase.setUserProperties(analytics, { role: userRole ?? "none" })
}

const setUp = async (config) => {
  const [{ initializeApp, getApps, getApp }, sdk] = await Promise.all([import("firebase/app"), import("firebase/analytics")])

  if (!(await sdk.isSupported())) {
    console.warn("Firebase Analytics is not supported in this browser (blocked storage or cookies); nothing is recorded.")
    return
  }

  const app = getApps().length ? getApp() : initializeApp(config)
  // send_page_view off: screen_view is sent per Inertia visit instead, with a route pattern, rather than
  // one automatic page_view carrying the real URL.
  const options = { config: { send_page_view: false, ...pageDefaults() } }
  try {
    analytics = sdk.initializeAnalytics(app, options)
  } catch {
    // Already initialised: a hot reload in development re-runs this module but keeps Firebase's app.
    analytics = sdk.getAnalytics(app)
  }
  firebase = sdk
  firebase.setDefaultEventParameters(pageDefaults())
  identify()
}

// Starts analytics once, with the web config Rails shared. Safe to call on every page: it runs once, and
// not at all without a config.
export const initAnalytics = (config) => {
  if (!isBrowser() || initPromise || !config?.apiKey || !config?.measurementId) return initPromise

  initPromise = setUp(config)
    .catch((error) => console.warn("Firebase Analytics did not start:", error))
    .finally(flush)
  return initPromise
}

export const setContext = (next, component = null) => {
  context = { ...next }
  screen = component
  if (analytics) firebase.setDefaultEventParameters(pageDefaults())
}

export const trackEvent = (name, params = {}) => {
  if (!isBrowser() || !name || !initPromise) return

  const enriched = enrich(params)
  if (!send(name, enriched) && pending.length < MAX_QUEUED_EVENTS) pending.push({ name, params: enriched })
}

// One per Inertia visit. `screen` is the page component ("Admin/Keys"), which is the route pattern for
// free: every app's keys page is one screen.
export const trackScreen = (screen, params = {}) => {
  trackEvent("screen_view", { screen_name: screen, screen_class: screen, ...params })
}

// The operator by id only, with their role as the one user property. Never their email or name.
export const identifyUser = (operator) => {
  const id = operator?.id ? String(operator.id) : null
  const role = operator?.role ?? null
  if (id === userId && role === userRole) return

  userId = id
  userRole = role
  identify()
}

export const resetUser = () => identifyUser(null)
